import { request } from './request';
import * as PATHS from '@/constants/path';
import { URLParamBuilder } from '@/utils/functions';
import { GetCustomersRequest } from '@/typings/api/getCustomers';
import { UnsubscribeCustomerRequest } from '@/typings/api/unsubscribeCustomer';
import { GetCustomerStatesRequest } from '@/typings/api/getCustomerStates';
import { ChangeCustomerStateRequest } from '@/typings/api/changeCustomerState';
import { ChangeCustomerNotesRequest } from '@/typings/api/changeCustomerNotes';
import { GetCustomerRequest } from '@/typings/api/getCustomer';
import { CustomerModel } from '@/typings/models/Customer';


// region Fetch Customers
type FetchCustomersForJourneyOptions = {
  params: GetCustomersRequest["parameters"],
  url?: string,
  query?: GetCustomersRequest["query"]
}

export function fetchCustomersForJourney({
                                           params,
                                           url,
                                           query = {}
                                         }: FetchCustomersForJourneyOptions) {
  let finalUrl: string;
  if (url) {
    // Merge query params (date, search, sort…) into the link URL so filters
    // are not lost when navigating to the next/prev page via API links.
    const parsed = new URL(url);
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        parsed.searchParams.set(key, String(value));
      }
    });
    finalUrl = parsed.toString();
  } else {
    finalUrl = URLParamBuilder(PATHS.GET_CUSTOMERS_API_PATH, {
      queryString: query,
      params
    });
  }
  return request<GetCustomersRequest["response"]>(finalUrl, {
    method: 'GET',
    absoluteURL: !!url
  });
}

// endregion

// region UnSubscribe
type UnsubscribeCustomerRequestOptions = {
  params: UnsubscribeCustomerRequest["parameters"]
}

export function unSubscribeCustomerRequest({
                                             params,
                                           }: UnsubscribeCustomerRequestOptions) {
  const url = URLParamBuilder(PATHS.UNSUBSCRIBE_CUSTOMER_API_PATH, {
    params
  })

  return request<any>(url, {
    method: 'POST',
    data: {
      subscribe: false
    }
  });
}

// endregion

// region Fetch Customer States
type FetchCustomerStatesOptions = {
  params: GetCustomerStatesRequest["parameters"]
}

export function fetchCustomerStatesRequest({
                                             params,
                                           }: FetchCustomerStatesOptions) {
  const url = URLParamBuilder(PATHS.GET_CUSTOMER_STATES_API_PATH, {
    params
  })

  return request<GetCustomerStatesRequest["response"]>(url, {
    method: 'GET',
  });
}

// endregion

// region Change Customer States
type ChangeCustomerStatesOptions = {
  params: ChangeCustomerStateRequest["parameters"]
  payload: ChangeCustomerStateRequest["payload"]
}

export function changeCustomerStateRequest({
                                             params,
                                             payload
                                           }: ChangeCustomerStatesOptions) {
  const url = URLParamBuilder(PATHS.CHANGE_CUSTOMER_STATE_API_PATH, {
    params
  })

  return request<ChangeCustomerStateRequest["response"]>(url, {
    method: 'POST',
    data: payload
  });
}

// endregion

// region Change Customer Notes
type ChangeCustomerNotesOptions = {
  params: ChangeCustomerNotesRequest["parameters"],
  payload: ChangeCustomerNotesRequest["payload"],
}

export function changeCustomerNotesRequest({
                                             params,
                                             payload
                                           }: ChangeCustomerNotesOptions) {
  const url = URLParamBuilder(PATHS.CHANGE_CUSTOMER_NOTES_API_PATH, {
    params
  })

  return request<ChangeCustomerStateRequest["response"]>(url, {
    method: 'POST',
    data: payload
  });
}

// endregion

// region Fetch Customer Details
// region Change Customer Notes
type FetchCustomerDetailsOptions = {
  params: GetCustomerRequest["parameters"],
}

export function fetchCustomerDetailsRequest({
                                              params,
                                            }: FetchCustomerDetailsOptions) {
  const url = URLParamBuilder(PATHS.GET_CUSTOMER_API_PATH, {
    params
  })

  return request<GetCustomerRequest["response"]>(url, {
    method: 'GET'
  });
}

// endregion

// region Fetch All Customers (general endpoint with sorting/filtering)
type FetchAllCustomersOptions = {
  url?: string,
  query?: Record<string, any>
}

export function fetchAllCustomers({
                                    url,
                                    query = {}
                                  }: FetchAllCustomersOptions) {
  const finalUrl = url || URLParamBuilder(PATHS.GET_ALL_CUSTOMERS_API_PATH, {
    queryString: query,
  })
  return request<any>(finalUrl, {
    method: 'GET',
    absoluteURL: !!url
  });
}
// endregion

// region Search company customers by name — companies are never deduplicated by name server-side
export function searchCompanyCustomersRequest(companyName: string) {
  const query = {
    search: companyName,
    customer_type: 'organization',
    with_leads: 1,
    page_size: 5,
    skip_total_size: 1,
  };
  return request<GetCustomersRequest["response"]>(
    URLParamBuilder(PATHS.GET_ALL_CUSTOMERS_API_PATH, { queryString: query }),
    { method: 'GET' },
  );
}
// endregion

// region Fetch one customer with its projects — the list search matches a customer uuid exactly
export function fetchCustomerWithLeadsRequest(customerId: string) {
  const query = {
    search: customerId,
    with_leads: 1,
    page_size: 5,
    skip_total_size: 1,
  };
  return request<GetCustomersRequest["response"]>(
    URLParamBuilder(PATHS.GET_ALL_CUSTOMERS_API_PATH, { queryString: query }),
    { method: 'GET' },
  );
}
// endregion

// region Create Customer (Laravel) — the flat personal fields become its primary contact.
export type CreateCustomerPayload = {
  type?: 'person' | 'organization';
  company_name?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  email?: string | null;
  phone?: string | null;
  locale?: string | null;
};

export function createCustomerRequest({ data }: { data: CreateCustomerPayload }) {
  return request<CustomerModel>(URLParamBuilder(PATHS.CREATE_CUSTOMER_API_PATH, {}, false), {
    method: 'POST',
    data,
  });
}
// endregion
